<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { orderService } from '@/services'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
import TextareaField from '@/components/TextareaField'
import NumberField from '@/components/NumberField'
import OrderInput from '@/components/OrderInput'
import Datatable from '@/components/Datatable'
import DatatableHeader from '@/components/DatatableHeader'
import DatatableBody from '@/components/DatatableBody'
// import DatatableFooter from '@/components/DatatableFooter'
import ProductImage from '@/components/ProductImage'
import ModalCheckoutOrder from '@/components/ModalCheckoutOrder'
import ModalDeleteOrder from '@/components/ModalDeleteOrder'
import ModalRemoveProduct from '@/components/ModalRemoveProduct'
import ModalClearCart from '@/components/ModalClearCart'
import ModalConfirmation from '@/components/ModalConfirmation'
import OrderStatusTracker from '@/components/OrderStatusTracker'
import ContentLoading from '@/components/ContentLoading'
import ProductDetail from '@/components/ProductDetail'
import SideBar from '@/components/SideBar'
import logger from '@/logger/index'
import { getUserErrorMessage } from '@/services/userError'

// Espera antes de salvar a quantidade. Nos botões −/+ é curta para agrupar
// cliques seguidos; na digitação é a mesma das Observações, para não salvar
// valores intermediários (ex.: o "1" antes do "15").
const ESPERA_SALVAR_BOTAO_MS = 500
const ESPERA_SALVAR_DIGITACAO_MS = 2000

export default {
  name: 'OrdersDetails',

  components: {
    Action,
    Icon,
    TextareaField,
    NumberField,
    OrderInput,
    Datatable,
    DatatableHeader,
    DatatableBody,
    // DatatableFooter,
    ProductImage,
    ModalRemoveProduct,
    ModalClearCart,
    ModalCheckoutOrder,
    ModalDeleteOrder,
    ModalConfirmation,
    OrderStatusTracker,
    ContentLoading,
    ProductDetail,
    SideBar,
  },

  data () {
    return {
      order: null,
      selectedProduct: null,
      obs: null,
      showModalRemoveProduct: false,
      showModalCheckoutOrder: false,
      showModalDeleteOrder: false,
      showModalConfirm: false,
      // Payload já formatado (product + quantity) que o aviso de estoque envia
      // ao confirmar; o selectedProduct tem só os dados crus do produto.
      stockConfirmationProduct: null,
      // Linha do carrinho que abriu o aviso de estoque; ao cancelar, a
      // quantidade exibida volta para a que está salva no pedido.
      stockConfirmationItem: null,
      loadingName: false,
      stockFavoriteCompany: null,
      statuses: [],
      statusesLoaded: false,
      showProductSidebar: false,
      loadingProductDetail: false,
      updatingQuantityProductId: null,
      isCartSummaryExpanded: false,
      quantitySaveTimers: {}, // timers de debounce por produto (keyed por search/id)
      quantitySaveStatus: {}, // status visual por produto: 'pending' | 'saving' | 'saved' | 'invalid'
      quantitySaveInFlight: {}, // single-flight: save em voo por produto
      quantitySavePending: {}, // single-flight: nova alteração chegou durante o voo (re-dispara ao fim)
      quantitySaveRequests: {}, // requisição em andamento por produto (para aguardar antes de sair/finalizar)
      quantitySaveProducts: {}, // produto que agendou o salvamento (o updateList pode trocar o objeto da lista)
      obsAutoSaveTimer: null,
      obsSaveStatus: null, // null, 'saving', 'saved'
      obsInitialValue: null,
      pendingObsSave: false,
      showModalClearCart: false,
    }
  },

  computed: {
    ...mapState(['headerBack']),
    ...mapGetters('orders', ['getSelectedOrder']),
    ...mapGetters('products', ['getSelectedProduct']),
    ...mapGetters('userPermissions', ['getFavoriteCompany', 'getBlockSale']),

    disabledProduction () {
      const hostname = window.location.hostname
      return hostname === 'tdrschererweb.scherer-sa.com.br'
    },

    priceRule () {
      return this.selectedProduct?.price?.find(price => this.selectedProduct.changeQuantity >= price.min_limit && this.selectedProduct.changeQuantity <= price.max_limit) || {}
    },

    orderTotal () {
      return this.order ? this.order.products.reduce((value, item) => {
        return value + (item.quantity * item.price)
      }, 0) : 0
    },

    orderTotalWithIPI () {
      return this.order ? this.order.products.reduce((value, item) => {
        return value + item.total_price_with_ipi
      }, 0) : 0
    },

    totalIpi () {
      return this.order ? this.order.products.reduce((value, item) => {
        return value + (item.total_price_with_ipi - item.quantity * item.price)
      }, 0) : 0
    },

    totalStockQuantity () {
      return this.selectedProduct?.stock.reduce(
        (total, stock) => total + stock.quantity,
        0
      )
    },

    hasIpiProducts () {
      return this.order.products.some(p => (p.product_ipi || 0) > 0)
    },

    ipiAliquota () {
      if (!this.order || !Array.isArray(this.order.products)) return 0
      const productWithIpi = this.order.products.find(product => Number(product.ipi_aliquota) > 0)
      return productWithIpi ? productWithIpi.ipi_aliquota : 0
    },

    canShowProductDetail () {
      return this.getSelectedProduct !== null
    },
  },

  watch: {
    showModalConfirm (aberto) {
      if (aberto || !this.stockConfirmationItem) return
      // Fechou sem salvar (Cancelar ou recusa da API): descarta a quantidade nova.
      this.stockConfirmationItem.changeQuantity = this.stockConfirmationItem.quantity
      this.stockConfirmationItem = null
    },

    orderTotal () {
      if (!this.order) return
      this.order.total = this.orderTotal
      // logger.debug('orderTotal: ', this.orderTotal)
    },

    getSelectedOrder: {
      handler (newOrder) {
        this.updateList(newOrder)
      },
      deep: true,
      immediate: true,
    },

    'order.products': {
      handler (newProducts) {
        // Quando os produtos são carregados, tenta exibir o tooltip
        if (newProducts && newProducts.length > 0) {
          this.$nextTick(() => {
            this.showProductInfoClickTooltip()
          })
        }
      },
      immediate: true,
    },

    getFavoriteCompany: {
      handler (newValue) {
        if (newValue && !this.statusesLoaded && this.order && this.order.status === 'Finalizado') {
          this.getStatuses()
        }
      },
      immediate: true,
    },

    'order.status': {
      handler (newStatus) {
        if (newStatus === 'Finalizado' && this.getFavoriteCompany && !this.statusesLoaded) {
          this.getStatuses()
        }
      },
    },

    obs (newValue, oldValue) {
      if (this.order && this.order.status !== 'Finalizado' && oldValue !== null && oldValue !== undefined) {
        this.handleObsChange(newValue)
      }
    },
  },

  async created () {
    this.setHeaderBack({ name: 'orders' })
    const { id } = this.$route.params
    await this.getAllOrders({ id })
    this.selectOrder({ id })
    if (this.getSelectedOrder) {
      this.updateList()
    } else {
      this.$router.push({ name: 'orders' })
    }
  },

  mounted () {
    // Reseta o scroll para o topo ao carregar o componente
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    // Exibe o tooltip sobre o clique no nome do produto (se order já estiver disponível)
    if (this.order && this.order.products && this.order.products.length > 0) {
      this.$nextTick(() => {
        this.showProductInfoClickTooltip()
      })
    }

    // Se o getFavoriteCompany já tem valor, carrega imediatamente
    if (this.getFavoriteCompany && !this.statusesLoaded && this.order && this.order.status === 'Finalizado') {
      this.getStatuses()
    }
  },

  beforeRouteLeave (to, from, next) {
    // Salva observações e quantidades pendentes antes de sair da rota; sem isso
    // a alteração que ainda aguardava a espera seria descartada no destroyed.
    Promise.all([this.ensureObsSaved(), this.ensureQuantitySaved()])
      .then(() => next())
      .catch(() => next())
  },

  destroyed () {
    if (this.obsAutoSaveTimer) {
      clearTimeout(this.obsAutoSaveTimer)
    }
    // Cancela timers de auto-save de quantidade pendentes
    Object.values(this.quantitySaveTimers).forEach(timer => timer && clearTimeout(timer))
    this.setHeaderBack(null)
  },

  methods: {
    ...mapActions(['setHeaderBack', 'setFeedback']),
    ...mapActions('orders', ['updateOrderName', 'updateOrderObs', 'selectOrder', 'updateProductQuantity', 'getAllOrders']),
    ...mapActions('products', ['checkProductStock', 'getProduct']),
    ...mapActions('userTips', ['showTip']),

    showProductInfoClickTooltip () {
      this.$nextTick(() => {
        let targetElement = null

        // Pegamos o primeiro elemento visualmente no DOM dentro desta tabela
        const componentRoot = this.$el
        if (componentRoot) {
          const firstProductInfoCell = componentRoot.querySelector('td[data-variant="product-info"]')
          if (firstProductInfoCell) {
            targetElement = firstProductInfoCell
          }
        }

        // Resolve o problema quando $refs não está na ordem visual correta
        if (!targetElement) {
          const refsArray = Array.isArray(this.$refs.productInfoCell)
            ? this.$refs.productInfoCell
            : (this.$refs.productInfoCell ? [this.$refs.productInfoCell] : [])

          if (refsArray.length > 0) {
            // Mapear todos os elementos válidos com suas posições
            const elementsWithPosition = refsArray
              .map(ref => {
                const element = ref?.$el || ref
                if (!element) return null
                const rect = element.getBoundingClientRect()
                return {
                  element,
                  top: rect.top + window.scrollY, // Posição absoluta no documento
                  index: refsArray.indexOf(ref),
                }
              })
              .filter(item => item !== null)

            elementsWithPosition.sort((a, b) => a.top - b.top)

            // Pega o primeiro elemento (mais próximo do topo)
            if (elementsWithPosition.length > 0) {
              targetElement = elementsWithPosition[0].element
            }
          }
        }

        if (targetElement) {
          this.showTip({
            key: 'accessProductDetail',
            title: 'Novo acesso Rápido ao Detalhes do Produto',
            content: 'Permite acessar rapidamente os detalhes do produto.',
            targetRef: targetElement,
            position: 'top',
            delay: 700,
            startDate: '18/11/2025',
            validForMonths: 3,
          })
        }
      })
    },

    resolveProductSearch (product) {
      return product?.id || product?.CODIGO || product?.product_id || product?.PRODUCT_ID || null
    },

    isUpdatingQuantity (product) {
      return this.updatingQuantityProductId === this.resolveProductSearch(product)
    },

    async openProductDetailSidebar (product) {
      if (this.loadingProductDetail) {
        return
      }

      const search = this.resolveProductSearch(product)

      if (!search) {
        this.setFeedback({
          title: 'Produto inválido',
          message: 'Não foi possível identificar o código do produto selecionado.',
          type: 'error',
        })
        return
      }

      this.loadingProductDetail = true

      try {
        const response = await this.getProduct({ search })

        if (response && !response.notFound) {
          this.showProductSidebar = true
        } else {
          this.setFeedback({
            title: 'Produto não encontrado',
            message: 'Não foi possível carregar os detalhes do produto selecionado.',
            type: 'error',
          })
        }
      } catch (error) {
        this.setFeedback({
          title: 'Erro ao carregar produto',
          message: 'Tente novamente em instantes.',
          type: 'error',
        })
      } finally {
        this.loadingProductDetail = false
      }
    },

    closeProductSidebar () {
      this.showProductSidebar = false
    },

    closeProductSidebarWithAnimation () {
      if (this.$refs.productSidebar) {
        this.$refs.productSidebar.closeWithAnimation()
      }
      this.showProductSidebar = false
    },

    calculateUnitPriceWithIpi (product) {
      if (product.product_ipi && product.product_ipi > 0) {
        const aliquota = product.price * (product.ipi_aliquota / 100)
        const priceWithIpi = product.price + aliquota
        return this.formatDecimal(priceWithIpi)
      }
      return this.formatDecimal(product.price)
    },

    calculateUnitIpiValue (product) {
      if (!product || !product.product_ipi || product.product_ipi <= 0) {
        return 0
      }

      const aliquota = product.price * (product.ipi_aliquota / 100)
      return this.formatDecimal(aliquota)
    },

    isMultipleOfFraction (frac, qty) {
      if (!frac || frac <= 0) return false

      if (qty < frac) return false

      if (qty === 0) return false

      return qty % frac === 0
    },

    updateList (sourceOrder = this.getSelectedOrder) {
      if (!sourceOrder) {
        this.order = null
        this.obs = null
        this.isCartSummaryExpanded = false
        return
      }

      const products = Array.isArray(sourceOrder.products)
        ? sourceOrder.products.map(product => ({
          ...product,
          changeQuantity: product.changeQuantity !== undefined ? product.changeQuantity : product.quantity,
        }))
        : []

      this.order = {
        ...sourceOrder,
        products,
        total: sourceOrder.products
          ? sourceOrder.products.reduce((value, item) => value + (item.quantity * item.price), 0)
          : 0,
      }

      this.obs = sourceOrder.obs || ''
      this.obsInitialValue = sourceOrder.obs || ''
      this.pendingObsSave = false
      this.obsSaveStatus = null
      if (this.obsAutoSaveTimer) {
        clearTimeout(this.obsAutoSaveTimer)
        this.obsAutoSaveTimer = null
      }
      this.isCartSummaryExpanded = false
    },

    setDeleting () {
      if (this.getSelectedOrder) {
        this.updateList()
      }
    },

    handleObsChange (newValue) {
      // Não salva se o valor não mudou em relação ao inicial
      const normalizedNewValue = newValue || ''
      const normalizedInitialValue = this.obsInitialValue || ''

      if (normalizedNewValue === normalizedInitialValue) {
        this.pendingObsSave = false
        this.obsSaveStatus = null
        if (this.obsAutoSaveTimer) {
          clearTimeout(this.obsAutoSaveTimer)
          this.obsAutoSaveTimer = null
        }
        return
      }

      // Cancela o timer anterior se existir
      if (this.obsAutoSaveTimer) {
        clearTimeout(this.obsAutoSaveTimer)
      }

      // Define que há uma alteração pendente
      this.pendingObsSave = true
      this.obsSaveStatus = 'saving'

      // Cria um novo timer com debounce de 2 segundos
      this.obsAutoSaveTimer = setTimeout(() => {
        this.saveObsAutomatic()
      }, 2000)
    },

    async saveObsAutomatic () {
      if (!this.order || this.order.status === 'Finalizado' || !this.pendingObsSave) {
        return Promise.resolve()
      }

      try {
        await this.updateOrderObs({
          id: this.order.id,
          obs: this.obs,
        })
        this.pendingObsSave = false
        this.obsSaveStatus = 'saved'
        this.obsInitialValue = this.obs

        // Remove o feedback após 3 segundos
        setTimeout(() => {
          if (this.obsSaveStatus === 'saved') {
            this.obsSaveStatus = null
          }
        }, 3000)
      } catch (error) {
        this.obsSaveStatus = null
        this.setFeedback({
          title: 'Erro ao salvar observações',
          message: 'Não foi possível salvar automaticamente. Tente novamente.',
          type: 'error',
        })
      }
    },

    async ensureObsSaved () {
      if (this.pendingObsSave && this.obsAutoSaveTimer) {
        clearTimeout(this.obsAutoSaveTimer)
        await this.saveObsAutomatic()
      }
    },

    handleSaveObs () {
      if (this.obsAutoSaveTimer) {
        clearTimeout(this.obsAutoSaveTimer)
      }
      this.updateOrderObs({
        id: this.order.id,
        obs: this.obs,
      }).then(() => {
        this.pendingObsSave = false
        this.obsInitialValue = this.obs
        this.obsSaveStatus = 'saved'
        setTimeout(() => {
          this.obsSaveStatus = null
        }, 3000)
        this.setFeedback({ title: 'Observação do pedido alterado com sucesso', message: '', type: 'success' })
      }).catch((error) => {
        this.obsSaveStatus = null
        const message = getUserErrorMessage(error, 'Erro ao salvar a observação do pedido')
        this.setFeedback({ title: 'Erro ao salvar a observação do pedido', message: message, type: 'error' })
      })
    },

    handleDeleteOrder () {
      this.showModalDeleteOrder = true
    },

    async handleCheckoutOrder () {
      // Garante que observações e quantidades sejam salvas antes de finalizar,
      // para o pedido não ser finalizado com uma quantidade antiga.
      await this.ensureObsSaved()
      await this.ensureQuantitySaved()
      // Se o salvamento abriu o aviso de estoque, o usuário decide primeiro.
      if (this.showModalConfirm) return
      this.showModalCheckoutOrder = true
    },

    openModalConfirmation () {
      this.showModalConfirm = true
    },

    handleSubmitName (value) {
      this.loadingName = true
      this.updateOrderName({
        id: this.order.id,
        name: value,
      }).then(() => {
        this.setFeedback({ title: 'Descrição do pedido alterado com sucesso', message: '', type: 'success' })
      }).catch((error) => {
        const message = getUserErrorMessage(error, 'Erro ao alterar a descrição do pedido')
        this.setFeedback({ title: 'Erro ao alterar a descrição do pedido', message: message, type: 'error' })
      }).finally(() => {
        this.loadingName = false
      })
    },

    getStockFavoriteCompany () {
      if (this.selectedProduct && this.selectedProduct.stock) {
        this.stockFavoriteCompany = this.selectedProduct?.stock.find(stock => stock.company_id === this.getFavoriteCompany)
      }
    },

    async updateQtdeProductToOrder (product) {
      // logger.warn('antes de enviar:', product)
      try {
        await this.updateProductQuantity(product)
        // product.quantity = product.changeQuantity
        logger.debug('atualizei a Qtde para: ', product.quantity)
        this.stockConfirmationItem = null
        this.$refs.modalConfirmation.closeModal()
        this.$refs.modalConfirmation.loading = false
        await this.updateList()
        this.setFeedback({ title: 'Produto atualizado com sucesso', message: '', type: 'success' })
      } catch (error) {
        this.$refs.modalConfirmation.loading = false
        const message = getUserErrorMessage(error, 'Tente novamente em instantes.')
        this.setFeedback({
          title: 'Erro ao atualizar quantidade',
          message: message,
          type: 'error',
        })
        return null
      }
    },

    async handleUpdateQuantity (product) {
      const search = this.resolveProductSearch(product)

      if (!search) {
        this.setFeedback({
          title: 'Produto inválido',
          message: 'Não foi possível identificar o código do produto selecionado.',
          type: 'error',
        })
        return
      }

      if (this.updatingQuantityProductId === search) {
        return
      }

      this.updatingQuantityProductId = search

      try {
        const productData = await this.getProduct({ search })

        if (!productData || productData.notFound) {
          this.setFeedback({
            title: 'Produto não encontrado',
            message: 'Não foi possível carregar os dados do produto para atualizar a quantidade.',
            type: 'error',
          })
          return
        }

        const productToCheck = {
          ...productData,
          changeQuantity: product.changeQuantity,
        }

        this.selectedProduct = productToCheck
        this.getStockFavoriteCompany()
        await this.checkBlockSale(productToCheck)
      } catch (error) {
        this.setFeedback({
          title: 'Erro ao atualizar quantidade',
          message: 'Tente novamente em instantes.',
          type: 'error',
        })
      } finally {
        this.updatingQuantityProductId = null
      }
    },

    // Retorna o status de salvamento da quantidade do produto (para feedback visual).
    quantityStatus (product) {
      const search = this.resolveProductSearch(product)
      return (search && this.quantitySaveStatus[search]) || null
    },

    // Auto-save da quantidade, no mesmo padrão das Observações
    // (handleObsChange/saveObsAutomatic). Substitui o antigo botão de confirmar:
    // ao alterar via −/+ ou digitação, agenda o salvamento sozinho. A espera
    // depende da origem: curta nos botões, igual às Observações na digitação.
    scheduleQuantitySave (product, origem) {
      const search = this.resolveProductSearch(product)
      if (!search) return
      if (this.order && this.order.status === 'Finalizado') return

      // Cancela o timer anterior deste produto
      if (this.quantitySaveTimers[search]) {
        clearTimeout(this.quantitySaveTimers[search])
        this.quantitySaveTimers[search] = null
      }

      // Sem alteração efetiva → limpa o status
      if (product.changeQuantity === product.quantity) {
        this.$set(this.quantitySaveStatus, search, null)
        return
      }

      // Valor inválido (não múltiplo da fração) → sinaliza e não agenda
      if (!this.isMultipleOfFraction(product.fraction || 1, product.changeQuantity)) {
        this.$set(this.quantitySaveStatus, search, 'invalid')
        return
      }

      // Marca como pendente e agenda o commit. O número já atualiza otimista na
      // tela; só o save espera.
      const espera = origem === 'digitacao' ? ESPERA_SALVAR_DIGITACAO_MS : ESPERA_SALVAR_BOTAO_MS
      this.$set(this.quantitySaveStatus, search, 'pending')
      this.quantitySaveProducts[search] = product
      this.quantitySaveTimers[search] = setTimeout(() => {
        this.commitQuantitySave(product)
      }, espera)
    },

    // Salva na hora a alteração que ainda aguardava a espera (ao sair do campo
    // ou apertar Enter), sem obrigar o usuário a esperar os 2s da digitação.
    // Usa o produto que agendou o salvamento, que carrega o valor alterado.
    flushQuantitySave (product) {
      const search = this.resolveProductSearch(product)
      if (!search || !this.quantitySaveTimers[search]) return Promise.resolve()
      clearTimeout(this.quantitySaveTimers[search])
      this.quantitySaveTimers[search] = null
      return this.commitQuantitySave(this.quantitySaveProducts[search] || product)
    },

    // Garante que nenhuma alteração de quantidade fique para trás: salva na hora
    // as que aguardavam a espera e aguarda as que já estão em andamento. Repete
    // algumas vezes porque um salvamento em andamento pode disparar outro, com
    // o valor mais novo, ao terminar.
    async ensureQuantitySaved () {
      for (let tentativa = 0; tentativa < 5; tentativa++) {
        const aguardando = Object.keys(this.quantitySaveTimers)
          .filter(search => this.quantitySaveTimers[search] && this.quantitySaveProducts[search])
          .map(search => this.quantitySaveProducts[search])
        const emAndamento = Object.values(this.quantitySaveRequests).filter(Boolean)
        if (!aguardando.length && !emAndamento.length) return

        await Promise.all([
          ...aguardando.map(product => this.flushQuantitySave(product)),
          ...emAndamento
        ]).catch(() => null)
      }
    },

    async commitQuantitySave (product) {
      const search = this.resolveProductSearch(product)
      if (!search) return
      this.quantitySaveTimers[search] = null

      // Revalida no momento do envio
      if (this.order && this.order.status === 'Finalizado') return
      if (product.changeQuantity === product.quantity) {
        this.$set(this.quantitySaveStatus, search, null)
        return
      }
      if (!this.isMultipleOfFraction(product.fraction || 1, product.changeQuantity)) {
        this.$set(this.quantitySaveStatus, search, 'invalid')
        return
      }

      // Single-flight por produto: se já há um save em voo, não dispara um segundo
      // concorrente — marca "pendente" para re-disparar com o valor mais novo ao
      // fim do voo atual. Evita perder alteração e empilhar requests.
      if (this.quantitySaveInFlight[search]) {
        this.quantitySavePending[search] = true
        return
      }

      this.quantitySaveInFlight[search] = true
      this.$set(this.quantitySaveStatus, search, 'saving')
      const requisicao = this.handleUpdateQuantity(product)
      this.quantitySaveRequests[search] = requisicao
      try {
        await requisicao
      } finally {
        this.quantitySaveInFlight[search] = false
        this.quantitySaveRequests[search] = null
      }

      // Se abriu o modal de confirmação (bloqueio de estoque), aguarda o usuário:
      // volta ao status neutro até a confirmação no modal.
      if (this.showModalConfirm) {
        this.stockConfirmationItem = product
        this.$set(this.quantitySaveStatus, search, null)
        this.quantitySavePending[search] = false
        return
      }

      // Chegou nova alteração durante o voo → re-dispara com o valor atual do
      // produto (single-flight trailing), sem empilhar requests.
      if (this.quantitySavePending[search]) {
        this.quantitySavePending[search] = false
        if (product.changeQuantity !== product.quantity) {
          return this.commitQuantitySave(product)
        }
      }

      // Feedback de "salvo" que some sozinho.
      this.$set(this.quantitySaveStatus, search, 'saved')
      setTimeout(() => {
        if (this.quantitySaveStatus[search] === 'saved') {
          this.$set(this.quantitySaveStatus, search, null)
        }
      }, 1800)
    },

    checkBlockSale (product) {
      logger.debug('Iniciando checkBlockSale: ', product)
      // logger.debug('quantidade atual:', product.quantity)
      // logger.debug('quantidade nova: ', product.changeQuantity)
      // logger.debug('stockFavorite: ', this.stockFavoriteCompany)
      // logger.debug('Empresa favorita - estoque: ', this.stockFavoriteCompany.quantity)
      // logger.debug('possui o estoque exigido: ', product.changeQuantity < this.stockFavoriteCompany.quantity)
      let formatedProduct = {
        list_type: this.priceRule.list_type,
        price: this.priceRule.price,
        changeQuantity: product.changeQuantity,
        product: product,
        quantity: product.changeQuantity,
      }
      logger.debug('formatedProduct: ', formatedProduct)
      if (this.stockFavoriteCompany && formatedProduct.changeQuantity > this.stockFavoriteCompany.quantity) {
        logger.debug('BLOQUEAR_VENDA: ', this.getBlockSale)
        return this.handleBlockSale(formatedProduct) // NOTE: Verifica o tipo de venda
      } else {
        logger.debug('A Qtde possui no estoque, irá inserir agora..')
        return this.updateQtdeProductToOrder(formatedProduct)
      }
    },

    handleBlockSale (product) {
      const totalStock = this.totalStockQuantity
      switch (this.getBlockSale) {
        case 0:
          logger.debug('case 0, vai atualizar a Qtde...')
          return this.updateQtdeProductToOrder(product)
        case 1:
          if (totalStock && product.changeQuantity > totalStock) {
            return this.setFeedback({ title: 'Não possui estoque disponível', message: 'Desculpe, mas não há mais unidades deste produto em estoque em todo Estado.', type: 'error' })
          }
          logger.debug('case 1, Abrindo o modal de confirmação...')
          this.stockConfirmationProduct = product
          return this.openModalConfirmation()
        case 2:
          logger.debug('case 2, Não possui estoque..')
          return this.setFeedback({ title: 'Não possui estoque disponível', message: 'Desculpe, mas não há mais unidades deste produto em estoque na filial preferencial.', type: 'error' })
        default:
          return []
      }
    },

    removeProduct (product) {
      this.selectedProduct = product
      this.showModalRemoveProduct = true
    },

    handleClearCart () {
      if (!this.order || !this.order.products || this.order.products.length === 0) return
      if (this.order.status === 'Finalizado') return
      this.showModalClearCart = true
    },

    handleClearCartConfirm () {
      this.updateList()
    },

    addProduct () {
      this.$router.push({ name: 'products' })
    },

    handleOpenPartsProductsFromSideBar () {
      // Fecha a sidebar de detalhes do produto
      this.closeProductSidebarWithAnimation()

      // Navega para a view de produtos com a aba de conjunto de peças selecionada
      setTimeout(() => {
        this.$router.push({
          name: 'products',
          query: { openModal: 'parts' },
        })
      }, 100)
    },

    toggleCartSummary () {
      this.isCartSummaryExpanded = !this.isCartSummaryExpanded
    },

    async getStatuses () {
      // Verifica se getFavoriteCompany tem valor válido
      if (!this.getFavoriteCompany) {
        logger.warn('getFavoriteCompany ainda não disponível, aguardando...')
        return
      }

      // Só busca statuses quando o pedido está finalizado
      if (!this.order || this.order.status !== 'Finalizado') {
        logger.debug('Pedido ainda não está finalizado, não buscando statuses')
        return
      }

      try {
        const { id } = this.$route.params
        const { data } = await orderService.getOrderTracking({ id: id })
        if (data) {
          this.statuses = data
          this.statusesLoaded = true
        }
      } catch (error) {
        this.statuses = []
      }
    },

  },
}
</script>

<template>
  <div :class="$classes['order-details']" v-if="order">
    <div :class="$classes['order-header']">
      <div :class="$classes['order-header-content']">
        <order-input :class="$classes['order-input']" :value="order.name" :loading="loadingName"
          @submit="handleSubmitName" @blur="handleSubmitName" />
        <div :class="$classes['order-header-actions']">
          <action v-if="$media.mobile" variant="flat" color="secondary" uppercase :class="$classes['order-back-btn']"
            :to="headerBack">
            <icon name="long-arrow-alt-left" btnIcon :class="$classes['order-back-btn-icon']" />
            Voltar
          </action>
          <action :disabled="order.status === 'Finalizado' || orderTotal === 0" color="tertiary" variant="flat"
            uppercase bold :class="$classes['order-finish-btn']" @click="handleCheckoutOrder">
            <icon id="link_finalizar" name="share" btnIcon />
            Finalizar pedido
          </action>
        </div>
        <!-- <div v-if="$media.mobile" :class="$classes['order-header-div']">
          <div v-if="$media.mobile" :class="$classes['order-header-total']">
            <span :class="$classes['order-header-total-label']">Total</span>
          </div>
          <div v-if="$media.mobile" :class="$classes['order-header-total-value']">
            <strong>{{ this.orderTotalWithIPI | currency }}</strong>
          </div>
        </div> -->
      </div>
    </div>
    <order-status-tracker v-if="statuses.length > 0" :current-status="order.status" :statuses="statuses" />
    <!-- Tablet/Mobile -->
    <div v-if="$media.tablet || $media.mobile" :class="$classes['order-content-tablet-mobile']">
      <action :class="$classes['order-button-filter']"
        :disabled="order.status === 'Finalizado' || !order.products.length" @click="handleClearCart">
        <span :class="$classes['menu-nav-icon-wrapper']">
          LIMPAR O CARRINHO
          <icon name="trash-alt" :class="$classes['btn-add-icon']" />
        </span>
      </action>
    </div>
    <!-- Desktop view -->
    <div :class="$classes['order-content']">
      <div v-if="$media.desktop" :class="$classes['order-content-table']">
        <div :class="$classes['order-content-table-header']">
          <h2 :class="$classes['order-content-table-title']">Produtos</h2>
          <action :class="$classes['order-content-table-clear-cart-btn']" variant="flat" uppercase
            :disabled="order.status === 'Finalizado' || !order.products.length" @click="handleClearCart">
            Limpar o carrinho
            <icon name="trash-alt" btnIcon :class="$classes['btn-add-icon']" />
          </action>
        </div>
        <datatable :items="order.products">
          <template slot="thead">
            <tr>
              <datatable-header width="100" text="" align="center" />
              <datatable-header text="Produto" align="left" />
              <datatable-header text="Qtd." align="left" />
              <datatable-header text="Preço" align="left" />
              <datatable-header text="Subtotal" align="left" />
              <datatable-header />
            </tr>
          </template>
          <template slot="tbody" slot-scope="props">
            <tr :class="$classes['order-tbody']">
              <datatable-body bold variant="product-image" :image="props.item.image" align="center" />
              <datatable-body ref="productInfoCell" :text="props.item.description" variant="product-info"
                :product="props.item" @click.native="openProductDetailSidebar(props.item)" />
              <datatable-body>
                <div :class="$classes['number-field']">
                  <div :class="[$classes['number-field-wrapper'], {
                    [$classes['has-changed']]: props.item.changeQuantity !== props.item.quantity,
                    [$classes['is-pending']]: ['pending', 'saving'].includes(quantityStatus(props.item)),
                    [$classes['is-saved']]: quantityStatus(props.item) === 'saved',
                    [$classes['is-invalid']]: quantityStatus(props.item) === 'invalid',
                    [$classes['is-disabled']]: order.status === 'Finalizado' || props.item.changeQuantity === 0
                  }]">
                    <number-field v-model="props.item.changeQuantity" :min="props.item.fraction"
                      :step="props.item.fraction" controls :disabled="order.status === 'Finalizado'"
                      @input="(valor, origem) => scheduleQuantitySave(props.item, origem)"
                      @blur="flushQuantitySave(props.item)" @submit="flushQuantitySave(props.item)" />
                    <svg v-if="['pending', 'saving'].includes(quantityStatus(props.item))"
                      :class="$classes['border-run']" viewBox="0 0 120 38" preserveAspectRatio="none" aria-hidden="true">
                      <rect :class="$classes['border-run-rect']" x="1" y="1" width="118" height="36" rx="7" ry="7"
                        pathLength="300" vector-effect="non-scaling-stroke" />
                    </svg>
                  </div>
                </div>
              </datatable-body>
              <datatable-body bold :text="props.item.price | currency">
                <template v-slot:text>
                  <br v-if="(props.item.product_ipi || 0) > 0">
                  <span v-if="(props.item.product_ipi || 0) > 0" :class="$classes['ipi-aliquota']">
                    + {{ calculateUnitIpiValue(props.item) | currency }}
                  </span>
                </template>
              </datatable-body>
              <datatable-body bold :text="(props.item.total_price_with_ipi) | currency" />
              <datatable-body align="center">
                <action :disabled="order.status === 'Finalizado'" color="secondary" variant="flat" uppercase
                  @click="removeProduct(props.item)">
                  <icon name="trash-alt" btnIcon />
                </action>
              </datatable-body>
            </tr>
          </template>
        </datatable>
        <div :class="$classes['price-card']">
          <div :class="$classes['price-card-left']">
            <action color="primary" variant="flat" uppercase bold :disabled="order.status === 'Finalizado'"
              @click="addProduct">
              <icon name="plus" btnIcon :class="$classes['btn-add-icon']" />
              Adicionar produtos
            </action>
          </div>
          <div :class="$classes['price-card-header']">

            <div v-if="hasIpiProducts" :class="$classes['price-summary']">
              <div :class="$classes['ipi-details']">
                <div :class="$classes['ipi-item']">
                  <span :class="$classes['ipi-label']">+ IPI</span>
                  <span :class="$classes['ipi-value']">{{ totalIpi | currency }}</span>
                </div>
                <div :class="$classes['subtotal-item']">
                  <span :class="$classes['subtotal-label']">Subtotal </span>
                  <span :class="$classes['subtotal-value']">{{ orderTotal | currency }}</span>
                </div>
              </div>
            </div>

            <div :class="$classes['price-summary']">
              <div :class="$classes['total-item']">
                <span :class="$classes['total-label']">Total</span>
                <span :class="$classes['total-value']">{{ orderTotalWithIPI | currency }}</span>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Mobile e Tablet view -->
      <template v-if="$media.mobile || $media.tablet">
        <div v-for="product in order.products" :key="product.id" :class="$classes['product']">
          <div :class="[$classes['product-columns'], $classes['gap']]">
            <div>
              <div v-if="product.image" :class="$classes['product-column']">
                <product-image :src="product.image" :width="100" :height="'auto'" />
              </div>
            </div>
            <div ref="productInfoCell" :class="$classes['product-main-info']"
              @click="openProductDetailSidebar(product)">
              <div v-if="product.id" :class="$classes['product-code']">
                Código: {{ product.id }}
                <div v-if="product.factory" :class="$classes['product-brand']">
                  {{ product.factory }}
                </div>
              </div>
              <div v-if="product.description" :class="$classes['product-description']">
                {{ product.description }}
              </div>
            </div>
          </div>
          <div :class="$classes['product-columns']">
            <div :class="$classes['product-column']">
              <span :class="$classes['product-column-label']">Qtd.</span>
              <div :class="$classes['number-field']">
                <div :class="[$classes['number-field-wrapper'], {
                  [$classes['has-changed']]: product.changeQuantity !== product.quantity,
                  [$classes['is-pending']]: ['pending', 'saving'].includes(quantityStatus(product)),
                  [$classes['is-saved']]: quantityStatus(product) === 'saved',
                  [$classes['is-invalid']]: quantityStatus(product) === 'invalid',
                  [$classes['is-disabled']]: order.status === 'Finalizado' || product.changeQuantity === 0
                }]">
                  <number-field v-model="product.changeQuantity" controls
                    :min="product.fraction || 1" :step="product.fraction || 1"
                    :disabled="order.status === 'Finalizado'"
                    @input="(valor, origem) => scheduleQuantitySave(product, origem)"
                    @blur="flushQuantitySave(product)" @submit="flushQuantitySave(product)" />
                  <svg v-if="['pending', 'saving'].includes(quantityStatus(product))"
                    :class="$classes['border-run']" viewBox="0 0 120 38" preserveAspectRatio="none" aria-hidden="true">
                    <rect :class="$classes['border-run-rect']" x="1" y="1" width="118" height="36" rx="7" ry="7"
                      pathLength="300" vector-effect="non-scaling-stroke" />
                  </svg>
                </div>
              </div>
            </div>
            <div :class="[$classes['product-column'], $classes['margin']]">
              <span :class="$classes['product-column-label']">Preço</span>
              <strong :class="$classes['product-column-value']">
                {{ product.price | currency }}
              </strong>
              <div v-if="(product.product_ipi || 0) > 0" :class="$classes['ipi-aliquota']">
                + {{ calculateUnitIpiValue(product) | currency }}
              </div>
            </div>
            <div :class="[$classes['product-column'], $classes['margin']]">
              <span :class="$classes['product-column-label']">Subtotal</span>
              <strong :class="$classes['product-column-value']">{{ (product.quantity * product.price) | currency
                }}</strong>
            </div>
          </div>
          <div :class="$classes['product-columns']">
            <action :disabled="order.status === 'Finalizado'" color="secondary" variant="flat" uppercase
              @click="removeProduct(product)">
              <icon name="trash-alt" btnIcon />
              Excluir
            </action>
          </div>
        </div>
        <div :class="$classes['price-card']" v-if="$media.tablet">
          <div :class="$classes['price-card-left']">
          </div>
          <div :class="$classes['price-card-header']">
            <div v-if="hasIpiProducts" :class="$classes['price-summary']">
              <div :class="$classes['ipi-details']">
                <div :class="$classes['ipi-item']">
                  <span :class="$classes['ipi-label']">+ IPI ({{ ipiAliquota }}%)</span>
                  <span :class="$classes['ipi-value']">{{ totalIpi | currency }}</span>
                </div>
                <div :class="$classes['subtotal-item']">
                  <span :class="$classes['subtotal-label']">Subtotal </span>
                  <span :class="$classes['subtotal-value']">{{ orderTotal | currency }}</span>
                </div>
              </div>
            </div>

            <div :class="$classes['price-summary']">
              <div :class="$classes['total-item']">
                <span :class="$classes['total-label']">Total</span>
                <span :class="$classes['total-value']">{{ orderTotalWithIPI | currency }}</span>
              </div>
            </div>
          </div>
        </div>
        <div :class="$classes['order-content-action']">
          <action color="primary" variant="flat" uppercase bold :disabled="order.status === 'Finalizado'"
            @click="addProduct">
            <icon name="plus" btnIcon :class="$classes['btn-add-icon']" />
            Adicionar produtos
          </action>
        </div>
      </template>

      <div :class="$classes['order-obs']">
        <div :class="$classes['order-obs-wrapper']">
          <textarea-field label="Observações" placeholder="Escreva aqui" v-model="obs"
            :disabled="order.status === 'Finalizado'" />
          <div v-if="obsSaveStatus"
            :class="[$classes['obs-save-status'], $classes[`obs-save-status--${obsSaveStatus}`]]">
            <icon v-if="obsSaveStatus === 'saved'" name="check" :class="$classes['obs-save-icon']" />
            <span :class="$classes['obs-save-text']">
              {{ obsSaveStatus === 'saving' ? 'Salvando...' : 'Salvo automaticamente' }}
            </span>
          </div>
        </div>
      </div>
      <div :class="$classes['order-actions']">
        <action :class="$classes['btn-gap']" color="primary" variant="outlined"
          :disabled="order.status === 'Finalizado'" @click="handleDeleteOrder">
          <icon name="trash-alt" />
          Excluir pedido
        </action>
        <action v-if="(obs || '') !== (obsInitialValue || '') && !pendingObsSave && obsSaveStatus !== 'saving'"
          color="tertiary" variant="outlined" @click="handleSaveObs">
          <icon name="check" />
          Salvar alterações
        </action>
      </div>
    </div>

    <div v-if="$media.mobile" :class="$classes['cart-summary-container']">
      <div :class="[$classes['cart-summary'], { [$classes['is-expanded']]: isCartSummaryExpanded }]">
        <button type="button" :class="$classes['cart-summary-handle']" @click="toggleCartSummary">
          <icon :name="isCartSummaryExpanded ? 'caret-down' : 'caret-up'"
            :class="$classes['cart-summary-handle-icon']" />
        </button>
        <button type="button" :class="$classes['cart-summary-toggle']" @click="toggleCartSummary">
          <div :class="$classes['cart-summary-header']">
            <div :class="$classes['cart-summary-title']">
              <icon name="clipboard-list" :class="$classes['cart-summary-title-icon']" />
              <span :class="$classes['cart-summary-title-text']">Resumo</span>
            </div>
            <div v-show="!isCartSummaryExpanded" :class="$classes['cart-summary-highlight']">
              <span :class="$classes['cart-summary-highlight-label']">Total</span>
              <strong :class="$classes['cart-summary-highlight-value']">{{ orderTotalWithIPI | currency }}</strong>
            </div>
          </div>
        </button>
        <div v-if="isCartSummaryExpanded" :class="$classes['cart-summary-body']">
          <div v-if="hasIpiProducts" :class="$classes['cart-summary-row']">
            <span :class="$classes['cart-summary-detail-label']">+ IPI</span>
            <span :class="$classes['cart-summary-detail-value']">{{ totalIpi | currency }}</span>
          </div>
          <div v-show="hasIpiProducts" :class="$classes['cart-summary-row']">
            <span :class="$classes['cart-summary-detail-label']">Subtotal</span>
            <span :class="$classes['cart-summary-detail-value']">{{ orderTotal | currency }}</span>
          </div>
          <div :class="[$classes['cart-summary-row'], $classes['cart-summary-total']]">
            <span :class="$classes['cart-summary-detail-label']">Total</span>
            <span :class="$classes['cart-summary-detail-value']">{{ orderTotalWithIPI | currency }}</span>
          </div>
        </div>
      </div>
    </div>

    <side-bar :active="showProductSidebar" @close="closeProductSidebar" ref="productSidebar">
      <template v-slot:body>
        <product-detail v-if="canShowProductDetail" @closeSideBarModalWithAnimation="closeProductSidebarWithAnimation"
          @openPartsProductsFromSideBar="handleOpenPartsProductsFromSideBar" />
        <content-loading v-else-if="loadingProductDetail" />
      </template>
    </side-bar>

    <modal-remove-product @delete="setDeleting" :active.sync="showModalRemoveProduct" :order="order"
      :product="selectedProduct" />

    <modal-checkout-order :active.sync="showModalCheckoutOrder" :order="order" />

    <modal-delete-order :active.sync="showModalDeleteOrder" :order="order" />

    <modal-clear-cart :active.sync="showModalClearCart" :order="order" @confirm="handleClearCartConfirm" />

    <modal-confirmation ref="modalConfirmation" :active.sync="showModalConfirm" :product="stockConfirmationProduct"
      :stock="stockFavoriteCompany && stockFavoriteCompany.quantity" @toOrder="updateQtdeProductToOrder" />
  </div>
</template>

<style module>
.order-details {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: calc(100vh - 64px);
}

:global(td[data-variant='product-info']) {
  cursor: pointer;
}

.order-header {
  padding-bottom: 0px;
  margin-bottom: 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border-radius: 0 0 20px 20px;
  position: relative;
  overflow: hidden;
}

.order-header-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  min-height: 100px;
  background: #fff;
  padding: 28px 36px;
  border-radius: 0 0 20px 20px;
  position: relative;
  backdrop-filter: blur(10px);
}

.order-header-content::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #4A94D4 0%, #3a7bc4 100%);
  border-radius: 0 0 20px 20px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(74, 148, 212, 0.3);
}

.order-content-tablet-mobile {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.order-content-tablet-mobile .order-button-filter {
  margin-left: 0;
}

.order-content-tablet-mobile .order-button-filter:not(:first-child) {
  margin-left: 15px;
}

.order-button-filter {
  border-radius: 30px;
  font-weight: 500;
  background-color: #ffffff;
  border: 1px solid #8f8b8b20;
}

.order-button-filter:hover:not(:disabled) {
  color: #1f2933;
  background-color: #f5f5f7;
  border-color: #cbd2d9;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.order-button-filter:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.menu-nav-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
  min-height: 42px;
  flex-shrink: 0;
  padding: 4px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #4A94D4;
  gap: 10px;
  line-height: 20px;
}

.menu-nav-icon-wrapper:hover:not(:disabled) {
  color: #4b7095;
}

.menu-nav-icon-wrapper .menu-nav-icon {
  margin-right: 8px;
  color: #8f8b8b;
}

.order-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
  margin: 0 auto;
}

.order-content-table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  padding: 16px 0;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.order-content-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 16px 16px;
}

.order-content-table-title {
  padding: 0;
  margin: 0;
  font-size: 32px;
  font-weight: 600;
  color: #333333;
}

.order-content-table-clear-cart-btn {
  font-size: 14px;
  font-weight: 600;
  color: #4A94D4;
  gap: 10px;
  /* text-decoration: underline; */
  line-height: 20px;
}

.order-content-table-clear-cart-btn:hover:not(:disabled) {
  color: #4b7095;
  background-color: #f5f5f7;
  border-color: #cbd2d9;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  border-radius: 30px;
  padding: 8px 0px 8px 15px;
  border: 1px solid #8f8b8b20;
}

tr.order-tbody>td {
  padding-top: 1.5em;
  padding-bottom: 1.5em;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

tr.order-tbody:hover>td {
  background-color: rgba(74, 148, 212, 0.06);
  transform: translateY(-1px);
}

.order-obs {
  background: #ffffff;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.order-obs:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border-color: rgba(74, 148, 212, 0.2);
}

.order-obs-wrapper {
  position: relative;
}

.obs-save-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  transition: opacity 0.3s ease;
  animation: fadeIn 0.3s ease;
}

.obs-save-status--saving {
  color: #4A94D4;
}

.obs-save-status--saved {
  color: #28a745;
}

.obs-save-icon {
  width: 14px;
  height: 14px;
  animation: checkmark 0.4s ease;
}

.obs-save-text {
  line-height: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes checkmark {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

.order-price {
  justify-content: flex-end;
}

.btn-icon {
  width: 20px;
  height: 20px;
  margin-right: 0px !important;
}

.btn-gap {
  gap: 10px;
}

.btn-add-icon {
  height: 18px;
  width: 18px;
}

.order-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 24px 28px;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.order-actions :global(button) {
  transition: all 0.3s ease;
  border-radius: 8px;
}

.order-actions :global(button:hover:not(:disabled)) {
  transform: translateY(-0px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.number-field {
  width: 122px;
}

.number-field-wrapper {
  display: inline-flex;
  align-items: center;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.number-field-wrapper:hover {
  border-color: #4A94D4;
  box-shadow: 0 4px 12px rgba(74, 148, 212, 0.15);
}

.number-field-wrapper.has-changed {
  border-color: #4A94D4;
  box-shadow: 0 0 0 3px rgba(74, 148, 212, 0.1);
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
}

.number-field-wrapper.is-disabled {
  border-color: rgba(162, 162, 162, 0.25);
  opacity: 0.6;
  background: #f8f9fa;
}

.number-field-wrapper .number-field-input {
  background: transparent;
  border: 0;
  height: 36px;
  max-width: 90px;
  flex: 1;
  font-weight: 600;
  color: #212529;
  padding: 0 12px;
}

.number-field-action {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #4A94D4 0%, #3a7bc4 100%);
  color: #fff;
  transition: all 0.3s ease;
  border-radius: 0 6px 6px 0;
}

.number-field-action:hover {
  background: linear-gradient(135deg, #3a7bc4 0%, #2d6ba3 100%);
  transform: scale(1.05);
}

.number-field-action.is-disabled {
  background: linear-gradient(135deg, #DBDBDB 0%, #c8c8c8 100%);
  cursor: not-allowed;
  transform: none;
}

.number-field-icon {
  width: 16px;
  height: 16px;
}

/*
 * Pendente/salvando: traço azul "correndo" por toda a borda (loading).
 * Feito com SVG + stroke-dashoffset porque garante velocidade uniforme ao longo
 * de todo o perímetro — inclusive nos cantos — o que o conic-gradient não dá num
 * retângulo largo e baixo (o facho "sumia" perto dos cantos/laterais).
 * A borda fica transparente pra não dar shift; o SVG desenha o traço por cima.
 */
.number-field-wrapper.is-pending {
  position: relative;
  border-color: transparent;
  overflow: visible;
}

.border-run {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  z-index: 2;
}

.border-run-rect {
  fill: none;
  stroke: #4A94D4;
  stroke-width: 2;
  stroke-linecap: round;
  /* traço de ~25% do perímetro + vão; base no perímetro real (~300, via pathLength) */
  stroke-dasharray: 75 225;
  animation: numberFieldBorderRun 1.2s linear infinite;
}

/* Salvo com sucesso: apenas o flash verde (sem ícone) */
.number-field-wrapper.is-saved {
  border-color: #28a745;
  animation: numberFieldSaved 0.7s ease-out;
}

/* Borda de alerta quando o valor digitado não é múltiplo da fração */
.number-field-wrapper.is-invalid {
  border-color: #D14040;
}

@keyframes numberFieldBorderRun {
  to { stroke-dashoffset: -300; }
}

@keyframes numberFieldSaved {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4);
    background: rgba(40, 167, 69, 0.14);
  }
  100% {
    box-shadow: 0 0 0 6px rgba(40, 167, 69, 0);
    background: #ffffff;
  }
}

/* Melhorias visuais para o header de ações */
.order-header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.order-finish-btn {
  transition: all 0.3s ease;
  border-radius: 8px;
  font-weight: 600;
  padding: 10px;
}

.order-finish-btn:hover:not(:disabled) {
  /* transform: translateY(-2px); */
  box-shadow: 0 4px 12px rgba(74, 148, 212, 0.3);
}

.order-back-btn {
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 10px;
}

.order-back-btn:hover {
  transform: translateX(-3px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

/* Destaque visual para o input do pedido */
.order-input {
  transition: all 0.3s ease;
  border-radius: 8px;
}

/* Card Principal de Preço */
.price-card {
  background: #ffffff;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease;
}

.price-card-left {
  display: flex;
  align-items: center;
}

.price-card-header {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.product-main-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
}

.product-code {
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  font-family: monospace;
}

.product-description {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #292929;
}

.product-brand {
  font-size: 14px;
  font-weight: 600;
  color: #4A94D4;
  text-transform: uppercase;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, Cantarell, "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
}

/* Resumo de Preços */
.price-summary {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 250px;
  min-height: 82px;
}

.ipi-details {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: #fff8f5;
  border-radius: 4px;
  border: 1px solid #ffe8dc;
}

.ipi-label {
  font-size: 12px;
  font-weight: 500;
  color: #dc3545;
  text-transform: uppercase;
}

.ipi-value {
  font-size: 14px;
  font-weight: 600;
  color: #dc3545;
}

.ipi-aliquota {
  display: inline-block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #dc3545;
}

.ipi-item,
.subtotal-item,
.total-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.subtotal-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  color: #212529;
}

.subtotal-value {
  font-size: 18px;
  font-weight: 700;
  color: #212529;
}

.total-item {
  padding: 12px 16px;
  flex: 1;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 4px;
}

.total-label {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  color: #495057;
  letter-spacing: 0.05em;
}

.total-value {
  font-size: 24px;
  font-weight: 700;
  color: #1976D2;
}

/* Estilos para Tablet */
@media (min-width: 768px) and (max-width: 1024px) {
  .order-content-tablet-mobile {
    margin: 0 24px 0;
  }

  .order-content {
    padding: 24px;
    max-width: 100%;
  }

  .order-content-table {
    padding: 12px 0;
    margin-bottom: 24px;
  }

  .order-content-table-header {
    padding: 0 12px 12px;
  }

  .order-content-table-title {
    font-size: 24px;
  }

  .order-header-content {
    padding: 24px 28px;
    min-height: 90px;
  }

  .order-header-actions {
    gap: 12px;
  }

  .order-finish-btn {
    padding: 8px 16px;
    font-size: 14px;
  }

  .order-obs {
    padding: 20px;
  }

  .order-actions {
    padding: 20px 24px;
    gap: 16px;
  }

  .number-field {
    width: 110px;
  }

  .number-field-wrapper {
    border-radius: 6px;
  }

  .number-field-input {
    height: 32px;
    max-width: 80px;
    padding: 0 10px;
    font-size: 13px;
  }

  .number-field-action {
    width: 36px;
    height: 36px;
  }

  .number-field-icon {
    width: 14px;
    height: 14px;
  }

  /* Estilos específicos para cards de produtos em tablets */
  .product {
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-bottom: 16px;
  }

  .product:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    transform: translateY(-3px);
    border-color: rgba(74, 148, 212, 0.2);
  }

  .product-columns {
    display: flex;
    align-items: stretch;
    gap: 20px;
  }

  .product-columns+.product-columns {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  .product-column {
    flex: 1;
  }

  .product-column.margin {
    margin: 10px;
  }

  .product-column-label {
    display: block;
    font-size: 12px;
    color: #6c757d;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: 0.08em;
  }

  .product-column-value {
    display: block;
    font-size: 14px;
    color: #212529;
    font-weight: 600;
    line-height: 1.5;
  }

  .order-content-action {
    display: flex;
    justify-content: center;
    margin: 32px 0;
  }
}

@media (max-width: 767px) {
  .order-header {
    background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
    padding: 0;
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-radius: 0 0 20px 20px;
  }

  .order-header-content {
    background: #fff;
    height: auto;
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    border-radius: 0 0 20px 20px;
    position: relative;
  }

  .order-header-content::before {
    height: 3px;
    border-radius: 0 0 20px 20px;
  }

  .order-header-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    order: 1;
    margin-bottom: 24px;
    width: 100%;
  }

  .order-input {
    order: 3;
    width: 100%;
  }

  .order-header-div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    order: 2;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 16px 20px;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .order-header-total {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    flex: 1;
    order: 2;
    font-size: 14px;
  }

  .order-header-total-value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    order: 2;
    font-size: 20px;
    font-weight: 700;
    color: #1976D2;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .order-header-total-label {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    color: #6c757d;
    letter-spacing: 0.08em;
  }

  .order-content-tablet-mobile {
    margin: 0 20px 0;
  }

  .order-content {
    padding: 20px;
    padding-bottom: 140px;
  }

  .order-content-action {
    display: flex;
    justify-content: center;
    margin: 32px 0;
  }

  .product {
    background: #fff;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .product:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    transform: translateY(-3px);
    border-color: rgba(74, 148, 212, 0.2);
  }

  .product+.product {
    margin-top: 16px;
  }

  .product-columns {
    display: flex;
    align-items: stretch;
  }

  .product-columns.gap {
    gap: 20px;
  }

  .product-columns+.product-columns {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  .product-column+.product-column {
    margin-left: 16px;
    flex: 1;
  }

  .product-column-label {
    display: block;
    font-size: 11px;
    color: #6c757d;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 10px;
    letter-spacing: 0.08em;
  }

  .product-column-value {
    display: block;
    font-size: 14px;
    padding-top: 10px;
    color: #212529;
    font-weight: 600;
    max-width: 165px;
    line-height: 1.5;
  }

  .order-obs {
    padding: 20px;
    border-radius: 12px;
  }

  .order-actions {
    flex-direction: column;
    gap: 16px;
  }

  .order-actions>* {
    width: 100%;
  }

  .cart-summary-container {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px 0 0 0;
    background: linear-gradient(180deg, rgba(248, 249, 250, 0) 0%, rgba(248, 249, 250, 0.85) 60%, rgba(248, 249, 250, 1) 100%);
    z-index: 20;
  }

  .cart-summary {
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.12);
    padding: 16px;
    transition: box-shadow 0.3s ease, transform 0.3s ease;
    position: relative;
  }

  .cart-summary.is-expanded {
    box-shadow: 0 -10px 28px rgba(0, 0, 0, 0.16);
    transform: translateY(-4px);
  }

  .cart-summary-handle {
    position: absolute;
    top: -18px;
    left: 50%;
    transform: translateX(-50%);
    width: 44px;
    height: 28px;
    background: #f8f9fa;
    border-radius: 14px 14px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1;
    border: none;
    cursor: pointer;
    color: inherit;
    padding: 0;
  }

  .cart-summary-handle-icon {
    width: 16px;
    height: 16px;
    color: #495057;
  }

  .cart-summary-toggle {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background: none;
    border: none;
    padding: 0;
    color: inherit;
  }

  .cart-summary-toggle:focus {
    outline: none;
  }

  .cart-summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
  }

  .cart-summary-title {
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    font-weight: 700;
    color: #1d3557;
    letter-spacing: 0.06em;
  }

  .cart-summary-title-icon {
    width: 18px;
    height: 18px;
    color: #1d3557;
  }

  .cart-summary-title-text {
    font-size: 12px;
  }

  .cart-summary-highlight {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .cart-summary-highlight-label {
    font-size: 10px;
    text-transform: uppercase;
    color: #6c757d;
    letter-spacing: 0.08em;
  }

  .cart-summary-highlight-value {
    font-size: 18px;
    font-weight: 700;
    color: #212529;
  }

  .cart-summary-values {
    display: flex;
    gap: 16px;
    justify-content: space-between;
    width: 100%;
  }

  .cart-summary-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .cart-summary-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6c757d;
    letter-spacing: 0.05em;
  }

  .cart-summary-value {
    font-size: 18px;
    font-weight: 700;
    color: #212529;
  }

  .cart-summary-body {
    margin-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cart-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
  }

  .cart-summary-detail-label {
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .cart-summary-detail-value {
    font-weight: 700;
    color: #212529;
  }

  .cart-summary-total {
    font-size: 18px;
    color: #1976D2;
  }
}

@media (max-width: 380px) {
  .order-back-btn-icon {
    display: none;
  }
}
</style>
